import {
  createContext,
  Dispatch,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { Personal } from '../store/store.entity';
import { CURRENT_PERSON_TARGET } from '../constants/common';

interface PersonalContextInterface {
  personal: Map<string, Personal>;
  selectedPerson?: string;
  setPersonal: Dispatch<React.SetStateAction<Map<string, Personal>>>;
  setSelectedPerson: Dispatch<string | undefined>;
  editPerson: (id: string, data: Personal) => void;
  newPerson: (data: Personal) => void;
  deletePerson: (id: string) => void;
  getPerson: (id: string) => Personal | undefined;
}

const PersonalContext = createContext<PersonalContextInterface | undefined>(
  undefined
);

export const PersonalContextProvider = ({
  children,
}: {
  children: ReactNode | ReactElement;
}) => {
  const [personal, setPersonal] = useState<Map<string, Personal>>(new Map());
  const [selectedPerson, setSelectedPerson] = useState<string>();

  const getPerson = (id: string) => {
    return personal.get(id);
  };

  const newPerson = (data: Personal) => {
    const personData = { ...data, id: uuid() };
    setPersonal((prevPersonal) => {
      const newPersonal = new Map(prevPersonal);
      newPersonal.set(personData.id, personData);
      return newPersonal;
    });
  };

  const editPerson = (id: string, data: Personal) => {
    setPersonal((prevPersonal) => {
      const newPersonal = new Map(prevPersonal);
      if (newPersonal.has(id)) {
        newPersonal.set(id, { ...newPersonal.get(id), ...data });
      }
      return newPersonal;
    });
  };

  const deletePerson = (id: string) => {
    setPersonal((prevPersonal) => {
      const newPersonal = new Map(prevPersonal);
      newPersonal.delete(id);
      return newPersonal;
    });
    sessionStorage.removeItem(CURRENT_PERSON_TARGET);
  };

  return (
    <PersonalContext.Provider
      value={{
        personal,
        setPersonal,
        getPerson,
        editPerson,
        newPerson,
        deletePerson,
        selectedPerson,
        setSelectedPerson,
      }}
    >
      {children}
    </PersonalContext.Provider>
  );
};

export const usePersonalContext = () => {
  const context = useContext(PersonalContext);
  if (!context) {
    throw new Error(
      "usePersonalContext can't used outside PersonalContextProvider"
    );
  }
  return context;
};
